<template>
    <div class="select-supplier-page">
        <div class="select-supplier-card">
            <div class="select-supplier-brand">
                <h1 class="select-supplier-title">{{ pageTitle }}</h1>
                <p class="select-supplier-subtitle">
                    {{ pageSubtitle }}
                </p>
            </div>
            <el-alert
                v-if="orderLinkOId && (orderLinkHint || orderHintLoading)"
                class="select-supplier-order-hint"
                :title="orderHintLoading ? '正在查询订单所属供应商…' : orderLinkHint"
                type="warning"
                :closable="false"
                show-icon
            />
            <div v-if="loading" class="select-supplier-loading">
                <i class="el-icon-loading"></i>
                <span>加载中…</span>
            </div>
            <div v-else-if="!suppliers.length" class="select-supplier-empty">
                <p>暂无可选供应商</p>
                <el-button type="primary" @click="goBack">{{ isSwitchMode ? "返回工作台" : "返回登录" }}</el-button>
            </div>
            <ul v-else class="supplier-list">
                <li
                    v-for="item in displaySuppliers"
                    :key="item.id"
                    class="supplier-item"
                    :class="{
                        'is-busy': selectingId === item.id,
                        'is-current': isSwitchMode && item.id === currentTuId,
                        'is-order-target': isOrderTargetSupplier(item),
                    }"
                    @click="selectSupplier(item)"
                >
                    <div class="supplier-item-main">
                        <span class="supplier-name">{{ item.name }}</span>
                        <span class="supplier-code">{{ item.code }}</span>
                    </div>
                    <el-tag
                        v-if="isOrderTargetSupplier(item)"
                        size="small"
                        type="danger"
                    >订单所属</el-tag>
                    <el-tag
                        v-else-if="isSwitchMode && item.id === currentTuId"
                        size="small"
                        type="warning"
                    >当前</el-tag>
                    <el-tag
                        v-else
                        size="small"
                        :type="item.can_maintain_hotel ? 'success' : 'info'"
                    >
                        {{ item.access_label }}
                    </el-tag>
                    <i
                        v-if="selectingId === item.id"
                        class="el-icon-loading supplier-item-spinner"
                    ></i>
                    <i v-else class="el-icon-arrow-right supplier-item-arrow"></i>
                </li>
            </ul>
            <div class="select-supplier-footer">
                <a href="javascript:;" @click="goBack">{{
                    isSwitchMode ? "取消，返回工作台" : "使用其他账号登录"
                }}</a>
            </div>
        </div>
    </div>
</template>

<script>
import qs from "qs";
import { getToken } from "../assets/js/auth";
import {
    clearPendingOrderLink,
    getPendingOrderLink,
    patchPendingOrderLink,
    redirectAfterAuth,
} from "@/utils/orderDeepLink";

const STORAGE_KEY_LOGIN = "ebooking_supplier_select";
const STORAGE_KEY_SWITCH = "ebooking_supplier_switch";

export default {
    name: "selectSupplier",
    data() {
        return {
            loading: true,
            mode: "login",
            da_id: "",
            pre_token: "",
            old_token: "",
            currentTuId: "",
            suppliers: [],
            selectingId: "",
            orderLinkOId: "",
            orderLinkTuId: "",
            orderLinkSupplierName: "",
            orderLinkInBinding: true,
            orderHintLoading: false,
        };
    },
    computed: {
        isSwitchMode() {
            return this.mode === "switch";
        },
        pageTitle() {
            return this.isSwitchMode ? "切换供应商" : "选择供应商";
        },
        pageSubtitle() {
            if (this.orderLinkHint) {
                return "请优先选择下方标注「订单所属」的供应商，以便打开微信推送的订单";
            }
            return this.isSwitchMode
                ? "请选择要切换的工作空间，切换后将进入对应供应商系统"
                : "您的账号已绑定以下供应商，请选择要进入的工作空间";
        },
        orderLinkHint() {
            if (!this.orderLinkOId) {
                return "";
            }
            if (this.orderLinkSupplierName) {
                if (!this.orderLinkInBinding) {
                    return (
                        "该订单属于【" +
                        this.orderLinkSupplierName +
                        "】，您的账号未绑定该供应商"
                    );
                }
                return (
                    "您打开的订单属于【" +
                    this.orderLinkSupplierName +
                    "】，请选择该供应商"
                );
            }
            if (this.orderLinkTuId) {
                return "您打开的订单需要指定供应商，请根据订单信息选择对应供应商";
            }
            return "您正在通过订单链接进入，请选择对应供应商";
        },
        displaySuppliers() {
            if (!this.orderLinkTuId || !this.suppliers.length) {
                return this.suppliers;
            }
            const tid = String(this.orderLinkTuId);
            const list = this.suppliers.slice();
            list.sort(function (a, b) {
                if (String(a.id) === tid) {
                    return -1;
                }
                if (String(b.id) === tid) {
                    return 1;
                }
                return 0;
            });
            return list;
        },
        storageKey() {
            return this.isSwitchMode ? STORAGE_KEY_SWITCH : STORAGE_KEY_LOGIN;
        },
    },
    mounted() {
        if (this.$route.query.mode === "switch") {
            this.mode = "switch";
            this.loadSwitchFromApi();
            return;
        }
        this.loadFromStorage();
    },
    methods: {
        loadOrderLinkHintFromPending() {
            const pending = getPendingOrderLink();
            const q = this.$route.query || {};
            const oIdFromPending =
                pending && pending.query && pending.query.o_id
                    ? String(pending.query.o_id)
                    : "";
            const oIdFromRoute =
                q.o_id != null && q.o_id !== "" ? String(q.o_id) : "";
            this.orderLinkOId = oIdFromPending || oIdFromRoute;
            if (!this.orderLinkOId) {
                return;
            }
            const tuFromPending =
                pending && pending.query && pending.query.tu_id
                    ? String(pending.query.tu_id)
                    : "";
            const tuFromRoute =
                q.tu_id != null && q.tu_id !== "" ? String(q.tu_id) : "";
            this.orderLinkTuId = tuFromPending || tuFromRoute;
            if (!this.orderLinkTuId || !this.suppliers.length) {
                return;
            }
            const tid = this.orderLinkTuId;
            const hit = this.suppliers.find(function (s) {
                return String(s.id) === tid;
            });
            if (hit && hit.name) {
                this.orderLinkSupplierName = hit.name;
            }
        },
        isOrderTargetSupplier(item) {
            if (!this.orderLinkTuId || !item) {
                return false;
            }
            return String(item.id) === String(this.orderLinkTuId);
        },
        loadFromStorage() {
            try {
                const raw = sessionStorage.getItem(STORAGE_KEY_LOGIN);
                if (!raw) {
                    this.loading = false;
                    return;
                }
                const payload = JSON.parse(raw);
                this.mode = "login";
                this.da_id = payload.da_id || "";
                this.pre_token = payload.pre_token || "";
                this.suppliers = payload.suppliers || [];
            } catch (e) {
                this.suppliers = [];
            }
            this.loadOrderLinkHintFromPending();
            this.loading = false;
            if (!this.da_id || !this.pre_token || !this.suppliers.length) {
                this.$message.warning("请先登录");
                this.goLogin();
                return;
            }
            if (this.orderLinkOId) {
                this.fetchOrderSupplierHint();
            }
        },
        fetchOrderSupplierHint() {
            if (!this.orderLinkOId || !this.da_id || !this.pre_token) {
                return;
            }
            this.orderHintLoading = true;
            const self = this;
            this.$axios
                .orderLinkSupplierHint(
                    qs.stringify({
                        o_id: this.orderLinkOId,
                        da_id: this.da_id,
                        pre_token: this.pre_token,
                    })
                )
                .then(function (res) {
                    if (!res || !res.tu_id) {
                        return;
                    }
                    self.orderLinkTuId = String(res.tu_id);
                    self.orderLinkSupplierName = res.supplier_name || "";
                    self.orderLinkInBinding = res.in_binding !== false;
                    if (!self.orderLinkSupplierName) {
                        const hit = self.suppliers.find(function (s) {
                            return String(s.id) === self.orderLinkTuId;
                        });
                        if (hit && hit.name) {
                            self.orderLinkSupplierName = hit.name;
                        }
                    }
                    patchPendingOrderLink({
                        o_id: self.orderLinkOId,
                        tu_id: self.orderLinkTuId,
                        supplier_name: self.orderLinkSupplierName,
                    });
                })
                .finally(function () {
                    self.orderHintLoading = false;
                });
        },
        loadSwitchFromApi() {
            const userInfoRaw = getToken("userInfo");
            if (!userInfoRaw) {
                this.loading = false;
                this.$message.warning("请先登录");
                this.goLogin();
                return;
            }
            let userInfo = {};
            try {
                userInfo = JSON.parse(userInfoRaw);
            } catch (e) {
                this.loading = false;
                this.goLogin();
                return;
            }
            this.old_token = userInfo.token || "";
            this.$axios
                .supplierPrepareSwitch(
                    qs.stringify({
                        da_id: userInfo.da_id || "",
                    })
                )
                .then((res) => {
                    this.loading = false;
                    if (res && res.need_select && res.suppliers && res.suppliers.length) {
                        this.da_id = res.da_id;
                        this.pre_token = res.pre_token;
                        this.suppliers = res.suppliers;
                        this.currentTuId = res.current_tu_id || userInfo.id || "";
                        try {
                            sessionStorage.setItem(
                                STORAGE_KEY_SWITCH,
                                JSON.stringify({
                                    mode: "switch",
                                    da_id: this.da_id,
                                    pre_token: this.pre_token,
                                    suppliers: this.suppliers,
                                    old_token: this.old_token,
                                    current_tu_id: this.currentTuId,
                                })
                            );
                        } catch (e) {
                            /* ignore */
                        }
                        return;
                    }
                    this.$message.warning(
                        (res && res.msg) || "暂无可切换的供应商"
                    );
                    this.goBack();
                })
                .catch(() => {
                    this.loading = false;
                    this.goBack();
                });
        },
        selectSupplier(item) {
            if (this.selectingId) {
                return;
            }
            if (this.isSwitchMode && item.id === this.currentTuId) {
                this.$message.info("当前已是该供应商");
                return;
            }
            this.selectingId = item.id;
            const payload = {
                da_id: this.da_id,
                tu_id: item.id,
                pre_token: this.pre_token,
            };
            if (this.isSwitchMode && this.old_token) {
                payload.old_token = this.old_token;
            }
            this.$axios
                .supplierSelectSupplier(qs.stringify(payload))
                .then((res) => {
                    this.selectingId = "";
                    if (res && res.token) {
                        sessionStorage.removeItem(STORAGE_KEY_LOGIN);
                        sessionStorage.removeItem(STORAGE_KEY_SWITCH);
                        this.setToken("userInfo", JSON.stringify(res));
                        if (
                            !redirectAfterAuth(this.$router, {
                                path: "/hotel",
                                query: Object.assign({}, this.$route.query),
                            })
                        ) {
                            this.$router.replace({
                                path: "/hotel",
                                query: Object.assign({}, this.$route.query),
                            });
                        }
                    }
                })
                .catch(() => {
                    this.selectingId = "";
                });
        },
        goLogin() {
            sessionStorage.removeItem(STORAGE_KEY_LOGIN);
            sessionStorage.removeItem(STORAGE_KEY_SWITCH);
            clearPendingOrderLink();
            this.removeToken("userInfo");
            this.$router.replace({ path: "/login" });
        },
        goBack() {
            if (this.isSwitchMode) {
                sessionStorage.removeItem(STORAGE_KEY_SWITCH);
                this.$router.replace({ path: "/hotel" });
                return;
            }
            this.goLogin();
        },
    },
};
</script>

<style scoped>
.select-supplier-page {
    width: 100%;
    min-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    box-sizing: border-box;
    background: linear-gradient(160deg, #eef2f8 0%, #f8f9fc 45%, #e8edf5 100%);
}

.select-supplier-card {
    width: 100%;
    max-width: 520px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(60, 80, 120, 0.12);
    padding: 32px 28px 24px;
}

.select-supplier-title {
    margin: 0 0 8px;
    font-size: 22px;
    font-weight: 600;
    color: #303133;
}

.select-supplier-subtitle {
    margin: 0 0 24px;
    font-size: 14px;
    color: #909399;
    line-height: 1.5;
}

.select-supplier-loading,
.select-supplier-empty {
    text-align: center;
    color: #909399;
    padding: 24px 0;
}

.supplier-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.supplier-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    margin-bottom: 10px;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
}

.supplier-item:hover {
    border-color: #93a5cf;
    background: #f5f7fb;
}

.supplier-item.is-current {
    border-color: #e6a23c;
    background: #fdf6ec;
}

.supplier-item.is-order-target {
    border-color: #f56c6c;
    background: #fef0f0;
}

.select-supplier-order-hint {
    margin-bottom: 16px;
}

.select-supplier-order-hint >>> .el-alert__title {
    font-size: 14px;
    line-height: 1.5;
    word-break: break-word;
}

.supplier-item.is-busy {
    pointer-events: none;
    opacity: 0.85;
}

.supplier-item-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.supplier-name {
    font-size: 15px;
    font-weight: 500;
    color: #303133;
}

.supplier-code {
    font-size: 12px;
    color: #909399;
}

.supplier-item-arrow {
    color: #c0c4cc;
    font-size: 16px;
}

.supplier-item-spinner {
    color: #93a5cf;
    font-size: 18px;
}

.select-supplier-footer {
    margin-top: 20px;
    text-align: center;
    font-size: 13px;
}

.select-supplier-footer a {
    color: #93a5cf;
    text-decoration: none;
}

.select-supplier-footer a:hover {
    text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// src/components/selectSupplier.vue